import React, { useState } from "react";
import ComponentCustomModal from "../../components/common/customModal/ComponentCustomModal";
import { FieldArray, Form, Formik } from "formik";
import { FtextField } from "../../components/common/FtextField";
import { DashCircle, PlusCircle } from "react-bootstrap-icons";
import Loader from "../../components/common/Loader";
import { createHotspotVoucher } from "../../features/hotspotApi";
import * as Yup from "yup";
import useISPowner from "../../hooks/useISPOwner";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

const VoucherFormDialog = ({ show, setShow }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const validationSchema = Yup.object({
    voucher: Yup.array().of(
      Yup.object({
        package: Yup.string(),
        quantity: Yup.number(),
      })
    ),
  });

  //---> Get user & current user data form useISPOwner hooks
  const { ispOwnerId } = useISPowner();

  const mikrotiks = useSelector((state) => state?.mikrotik?.mikrotik);

  const hotsPackage = useSelector((state) => state.hotspot?.package);

  //---> Loading State
  const [isLoading, setIsLoading] = useState(false);
  const [defaultValues, setDefaultValue] = useState({
    voucher: [
      {
        mikrotik: "",
        package: "",
        quantity: 0,
        price: 0,
        total: 0,
      },
    ],
  });

  const formValueHandlerFunction = (
    { target: { value, name } },
    index,
    values,
    setFieldValue
  ) => {
    let { quantity, price } = values.voucher[index] || {};
    let { grandTotal, dueAmount } = values;

    if (name === `voucher.${index}.package`) {
      const findPackage = hotsPackage?.find((item) => item.id === value);

      setFieldValue(
        `voucher.${index}.price`,
        findPackage ? findPackage.rate : 0
      );
    }

    if (name === `voucher.${index}.quantity`) {
      const total = Math.round(Number(value) * price);

      values.voucher[index].quantity = Number(value);
      values.voucher[index].total = Number(total);
      setFieldValue(`voucher.${index}.total`, total);
    }

    if (name === `voucher.${index}.price`) {
      const total = Math.round(Number(value) * quantity);

      values.voucher[index].price = Number(value);
      values.voucher[index].total = Number(total);
      setFieldValue(`voucher.${index}.total`, total);
    }

    let totalAmount = 0;
    let totalBill = 0;
    values.voucher.forEach((_, _index) => {
      const fieldTotal = values.voucher[_index].total || 0;

      totalAmount += fieldTotal;
      totalBill = totalAmount + values.previousDue;
    });

    setFieldValue("totalAmount", totalAmount);
    setFieldValue("grandTotal", totalBill);
  };

  const handleCreateVoucherSubmit = (formValues) => {
    const sendData = {
      ...formValues,
      ispOwner: ispOwnerId,
    };

    createHotspotVoucher(dispatch, sendData, setIsLoading);
  };

  return (
    <>
      <ComponentCustomModal show={show} setShow={setShow} size="xl">
        <Formik
          initialValues={defaultValues}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            handleCreateVoucherSubmit(values);
          }}
          enableReinitialize
        >
          {({ values, setFieldValue }) => (
            <Form>
              <div className="displayGrid mt-3">
                <table className="table align-middle">
                  <thead className="text-center">
                    <tr>
                      <th style={{ width: "25%" }} className="border">
                        {t("mikrotik")}
                      </th>
                      <th style={{ width: "25%" }} className="border">
                        {t("package")}
                      </th>
                      <th style={{ width: "15%" }} className="border">
                        {t("quantity")}
                      </th>
                      <th style={{ width: "15%" }} className="border">
                        {t("price")}
                      </th>
                      <th style={{ width: "15%" }} className="border">
                        {t("total")}
                      </th>
                      <th style={{ width: "5%" }} className="border">
                        {t("action")}
                      </th>
                    </tr>
                  </thead>

                  <FieldArray name="voucher">
                    {({ push, remove }) => (
                      <tbody>
                        {values.voucher.map((field, index) => (
                          <tr key={index}>
                            <td className="border">
                              <FtextField
                                {...{
                                  name: `voucher.${index}.mikrotik`,
                                  as: "select",
                                  id: `voucher.${index}.mikrotik`,
                                  options: mikrotiks.map((item) => ({
                                    text: item.name,
                                    value: item.id,
                                  })),
                                  firstOptions: t("mikrotik"),
                                  textAccessor: "text",
                                  valueAccessor: "value",
                                }}
                              />
                            </td>

                            <td className="border">
                              <FtextField
                                {...{
                                  name: `voucher.${index}.package`,
                                  as: "select",
                                  id: `voucher.${index}.package`,
                                  options: hotsPackage
                                    ?.filter(
                                      (item) =>
                                        item.mikrotik ===
                                        values.voucher[index]?.mikrotik
                                    )
                                    ?.map((item) => ({
                                      text: item.name,
                                      value: item.id,
                                    })),
                                  firstOptions: t("package"),
                                  textAccessor: "text",
                                  valueAccessor: "value",
                                  onChange: (event) =>
                                    formValueHandlerFunction(
                                      event,
                                      index,
                                      values,
                                      setFieldValue
                                    ),
                                }}
                              />
                            </td>

                            <td className="border">
                              <FtextField
                                {...{
                                  name: `voucher.${index}.quantity`,
                                  type: "text",
                                  id: `voucher.${index}.quantity`,
                                  className: "form-control text-center",
                                  onChange: (event) =>
                                    formValueHandlerFunction(
                                      event,
                                      index,
                                      values,
                                      setFieldValue
                                    ),
                                }}
                              />
                            </td>

                            <td className="border">
                              <FtextField
                                {...{
                                  name: `voucher.${index}.price`,
                                  type: "text",
                                  id: `voucher.${index}.price`,
                                  className: "form-control text-center",
                                  onChange: (event) =>
                                    formValueHandlerFunction(
                                      event,
                                      index,
                                      values,
                                      setFieldValue
                                    ),
                                }}
                              />
                            </td>

                            <td className="border">
                              <FtextField
                                {...{
                                  name: `voucher.${index}.total`,
                                  type: "text",
                                  id: `voucher.${index}.total`,
                                  className: "form-control text-center",
                                }}
                              />
                            </td>

                            <td className="text-center border">
                              {index + 1 === values.voucher.length ? (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-primary"
                                  onClick={() =>
                                    push({
                                      mikrotik: "",
                                      package: "",
                                      quantity: 0,
                                      price: 0,
                                      total: 0,
                                    })
                                  }
                                >
                                  <PlusCircle size={20} />
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-danger text-center"
                                  onClick={() => remove(index)}
                                >
                                  <DashCircle size={20} />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}

                        <tr>
                          <td colSpan="3" className="border-0"></td>
                          <td className="border" style={{ fontWeight: 650 }}>
                            {t("totalAmount")}
                          </td>
                          <td className="border">
                            <FtextField
                              {...{
                                name: "totalAmount",
                                type: "text",
                                id: "totalAmount",
                              }}
                            />
                          </td>
                        </tr>

                        {/*   <tr>
                              <td colSpan="3" className="border-0"></td>
                              <td
                                className="border"
                                style={{ fontWeight: 550 }}
                              >
                                {t("previousDue")}
                              </td>
                              <td className="border">
                                <FtextField
                                  {...{
                                    name: "previousDue",
                                    type: "text",
                                    id: "previousDue",
                                    onChange: (event) =>
                                      formValueHandlerFunction(
                                        event,
                                        null,
                                        values,
                                        setFieldValue
                                      ),
                                  }}
                                />
                              </td>
                            </tr>

                            <tr>
                              <td colSpan="3" className="border-0"></td>
                              <td
                                className="border"
                                style={{ fontWeight: 600 }}
                              >
                                {t("grandTotal")}
                              </td>
                              <td className="border">
                                <FtextField
                                  {...{
                                    name: "grandTotal",
                                    type: "text",
                                    id: "grandTotal",
                                  }}
                                />
                              </td>
                            </tr>

                            {isUpdate && (
                              <tr>
                                <td colSpan="3" className="border-0"></td>
                                <td
                                  className="border"
                                  style={{ fontWeight: 650 }}
                                >
                                  {t("dueAmount")}
                                </td>
                                <td className="border">
                                  <FtextField
                                    {...{
                                      name: "dueAmount",
                                      type: "text",
                                      id: "dueAmount",
                                    }}
                                  />
                                </td>
                              </tr>
                            )} */}
                      </tbody>
                    )}
                  </FieldArray>
                </table>
              </div>

              <div className="displayGrid1 float-end mt-4">
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={isLoading}
                  onClick={() => setShow(false)}
                >
                  {t("cancel")}
                </button>
                <button
                  type="submit"
                  className="btn btn-success customBtn"
                  disabled={isLoading}
                >
                  {isLoading ? <Loader /> : t("create")}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </ComponentCustomModal>
    </>
  );
};

export default VoucherFormDialog;
